export interface WarehouseChargeItem {
  id?: string | null;
  type?: string;
  name?: string;
  label?: string;
  category_name?: string;
  quantity?: number;
  count?: number;
  amount_excl_tax?: number;
  amount_incl_tax?: number;
  amount_after_tax?: number;
  currency?: string;
  consumed_at?: string;
  canceled_at?: string | null;
}

export interface DailyChargeGroup {
  date: string | null;
  dateLabel: string;
  accommodation: WarehouseChargeItem[];
  options: WarehouseChargeItem[];
  accommodationTotal: number;
  optionsTotal: number;
}

export function isSearchReservationSource(source?: string): boolean {
  return source === "searchReservations" || source === "getReservationById";
}

export function extractChargeItemsFromPayload(payload: unknown): WarehouseChargeItem[] {
  if (!payload || typeof payload !== "object") {
    return [];
  }

  const items = (payload as { charge_items?: unknown }).charge_items;
  return Array.isArray(items) ? (items as WarehouseChargeItem[]) : [];
}

export function extractCurrencyFromPayload(payload: unknown): string {
  if (!payload || typeof payload !== "object") {
    return "EUR";
  }

  const record = payload as { currency_code?: string; charge_items?: WarehouseChargeItem[] };
  if (record.currency_code) {
    return record.currency_code;
  }

  const first = record.charge_items?.find(item => item.currency);
  return first?.currency || "EUR";
}

function extractDateKey(consumedAt: string | undefined): string | null {
  if (!consumedAt) {
    return null;
  }

  const match = consumedAt.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match?.[1]) {
    return match[1];
  }

  const parsed = new Date(consumedAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString().slice(0, 10);
}

function formatDayLabel(dateKey: string | null): string {
  if (!dateKey) {
    return "Undated";
  }

  const parsed = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return dateKey;
  }

  return parsed.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function isAccommodationCharge(item: WarehouseChargeItem): boolean {
  return (item.type || "").toUpperCase() === "ACCOMMODATION";
}

function chargeAmount(item: WarehouseChargeItem): number {
  return item.amount_after_tax ?? item.amount_incl_tax ?? item.amount_excl_tax ?? 0;
}

function chargeLabel(item: WarehouseChargeItem): string {
  const base = item.label || item.name || item.category_name || item.type || "Charge";
  const quantity = item.quantity ?? item.count;
  if (quantity != null && quantity > 1) {
    return `${base} × ${quantity}`;
  }
  return base;
}

export function groupChargeItemsByDay(items: WarehouseChargeItem[]): DailyChargeGroup[] {
  const activeItems = items.filter(item => !item.canceled_at);
  const groups = new Map<string | null, DailyChargeGroup>();

  for (const item of activeItems) {
    const dateKey = extractDateKey(item.consumed_at);
    const existing = groups.get(dateKey) ?? {
      date: dateKey,
      dateLabel: formatDayLabel(dateKey),
      accommodation: [],
      options: [],
      accommodationTotal: 0,
      optionsTotal: 0,
    };

    const amount = chargeAmount(item);
    if (isAccommodationCharge(item)) {
      existing.accommodation.push(item);
      existing.accommodationTotal += amount;
    } else {
      existing.options.push(item);
      existing.optionsTotal += amount;
    }

    groups.set(dateKey, existing);
  }

  return [...groups.values()].sort((a, b) => {
    if (a.date == null) {
      return 1;
    }
    if (b.date == null) {
      return -1;
    }
    return a.date.localeCompare(b.date);
  });
}

export function getChargeItemLabel(item: WarehouseChargeItem): string {
  return chargeLabel(item);
}

export function getChargeItemAmount(item: WarehouseChargeItem): number {
  return chargeAmount(item);
}
